import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Container, Row, Col, Card, Modal } from "react-bootstrap";
import BusinessIcon from '@material-ui/icons/Business';
import PeopleIcon from '@material-ui/icons/People';
import ScheduleIcon from '@material-ui/icons/Schedule';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import { Empresa } from "../Empresa";
import { Horarios } from "../HorariosEspecialistas";
import { GastosFijos } from "../GastosFijos"; // Importa GastosFijos
import CheckCircleOverlay from '../../components/CheckCircleOverlay';
import { saveWizardConfig, getWizardConfig } from "../../services/firebase/wizardconfig";

const dashboardItems = [
  {
    label: "Datos del Consultorio",
    icon: <BusinessIcon style={{ fontSize: 64, color: '#007bff' }} />,
    bg: "primary",
    key: "empresa"
  },
  
  {
    label: "Horarios",
    icon: <ScheduleIcon style={{ fontSize: 64, color: '#ffc107' }} />,
    bg: "warning",
    key: "horarios",
    to: "/horarios"
  },
  {
    label: "Gastos Fijos",
    icon: <MonetizationOnIcon style={{ fontSize: 64, color: '#17a2b8' }} />,
    bg: "info",
    key: "gastosFijos",
    to: "/gastos-fijos"
  }
];

function getInitialSteps() {
  return dashboardItems.map(item => ({
    form: item.key,
    completed: false
  }));
}

export default function WizardConfig() {
  const empresaId = useSelector(state => state.empresa?.id || "");

  // Estado de pasos completados como arreglo dinámico
  const [completedObj, setCompletedObj] = useState({
    empresaId: empresaId,
    steps: getInitialSteps()
  });

  const [showEmpresa, setShowEmpresa] = useState(false);
  const [showHorarios, setShowHorarios] = useState(false);
  const [showGastosFijos, setShowGastosFijos] = useState(false); // Nuevo estado para modal GastosFijos

  // Leer el progreso al montar el componente
  React.useEffect(() => {
    const fetchWizardConfig = async () => {
      if (!empresaId) return;
      const data = await getWizardConfig(empresaId);
      if (data && Array.isArray(data.steps)) {
        setCompletedObj(data);
      } else {
        // Si no hay datos, inicializa con los pasos actuales
        setCompletedObj({
          empresaId,
          steps: getInitialSteps()
        });
      }
    };
    fetchWizardConfig();
  }, [empresaId]);

  // Función para marcar un paso como completado y persistir
  const handleStepCompleted = async (key) => {
    const updatedSteps = completedObj.steps.map(step =>
      step.form === key ? { ...step, completed: true } : step
    );
    const updated = {
      ...completedObj,
      empresaId,
      steps: updatedSteps
    };
    setCompletedObj(updated);
    try {
      await saveWizardConfig(updated);
    } catch (err) {
      console.error("Error guardando progreso del wizard:", err);
    }
  };

  // Helper para saber si un paso está completado
  const isStepCompleted = (key) => {
    const step = completedObj.steps.find(s => s.form === key);
    return step ? step.completed : false;
  };

  const handleCardClick = (item) => {
    if (item.key === "empresa") {
      setShowEmpresa(true);
    } else if (item.key === "horarios") {
      setShowHorarios(true);
    } else if (item.key === "gastosFijos") {
      setShowGastosFijos(true);
    } else if (item.to) {
      window.location.href = item.to;
      // Si quieres marcar como completado al navegar, descomenta:
      // handleStepCompleted(item.key);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center mb-4">
        <Col xs={12}>
          <h2 className="text-center fw-bold mb-3" style={{ color: '#0d6efd' }}>
            Bienvenido a tu plataforma Sysdent
          </h2>
          <p className="text-center lead mb-4">
            Vamos a iniciar con la configuración de tu consultorio virtual!
          </p>
        </Col>
      </Row>
      <Row className="justify-content-center g-4">
        {dashboardItems.map((item, idx) => (
          <Col xs={12} sm={6} md={4} lg={3} key={item.label} style={{ position: 'relative' }}>
            <Card className={`text-center shadow h-100 border-0 bg-light`} style={{ cursor: 'pointer', transition: 'transform 0.2s', position: 'relative' }}
              onClick={() => handleCardClick(item)}
              onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              <Card.Body className="d-flex flex-column align-items-center justify-content-center" style={{ position: 'relative' }}>
                {item.icon}
                {isStepCompleted(item.key) && (
                  <CheckCircleOverlay size={32} style={{ right: 8, top: 8 }} />
                )}
                <Card.Title className="mt-3 mb-0" style={{ fontSize: 22 }}>{item.label}</Card.Title>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      <Modal show={showEmpresa} onHide={() => setShowEmpresa(false)} size="lg" centered fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>Datos Empresa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Empresa onSaved={() => { setShowEmpresa(false); handleStepCompleted("empresa"); }} />
        </Modal.Body>
      </Modal>
      <Modal show={showHorarios} onHide={() => setShowHorarios(false)} size="lg" centered fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>Configurar Horarios</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Horarios onSaved={() => { setShowHorarios(false); handleStepCompleted("horarios"); }} />
        </Modal.Body>
      </Modal>
      <Modal show={showGastosFijos} onHide={() => setShowGastosFijos(false)} size="lg" centered fullscreen="md-down">
        <Modal.Header closeButton>
          <Modal.Title>Gastos Fijos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <GastosFijos onSaved={() => { setShowGastosFijos(false); handleStepCompleted("gastosFijos"); }} />
        </Modal.Body>
      </Modal>
      <CheckCircleOverlay />
    </Container>
  );
}
